import React, {useEffect, useState} from 'react';
import {
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Snackbar,
  Box,
  Popover,
  Alert,
  Grid,
} from '@mui/material';
import {SettingsCard} from '../../components/SettingsCard';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {DateField} from '@mui/x-date-pickers';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  useGetOrgQuery,
  useGetStockMarketDataQuery,
  useUpdateOrgMutation,
} from '../../data/api';
import {ColorPicker, useColor} from 'react-color-palette';
import 'react-color-palette/css';

const INDUSTRY_OPTIONS = [
  'Software',
  'Manufacturing',
  'Insurance',
  'Financial Services',
  'Real Estate',
  'Health Care',
  'Franchisors',
  'Entertainment',
  'Other',
];
const styles = {
  header: {
    paddingTop: '5rem',
    paddingLeft: '3rem',
    height: '100%',
  },
  headerTitle: {
    color: '#162523',
    fontSize: '32px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '120%',
  },
  divider: {
    marginLeft: '-3rem !important',
    marginTop: '1.5rem !important',
    marginBottom: '2rem !important',
    paddingRight: '-3rem !important',
    border: '0.5px solid rgba(0, 0, 0, 0.87) !important',
  },
  datePicker: {
    height: '3.45rem !important',
    width: '100%',
    borderRadius: '8px',
    marginTop: '0.5rem',
    '.MuiOutlinedInput-root.Mui-focused fieldset': {
      border: '2px solid #34A290',
    },
    '.MuiOutlinedInput-notchedOutline': {
      '&.Mui-focused': {
        border: '2px solid #34A290',
      },
    },
    '.MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
    '&.Mui-selected': {
      backgroundColor: '#34A290',
    },
    '& .MuiInputBase-input': {
      fontWeight: '400',
    },
  },
  select: {
    height: '3.45rem',
    width: '100%',
    marginTop: '0.5rem',
    borderRadius: '8px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: '2px solid #34A290 !important',
    },
    '.MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
    '& .MuiInputBase-input': {
      fontWeight: '400',
    },
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    lineHeight: '140%',
  },
  peerCompaniesAutocomplete: {
    width: '100%',
    marginTop: '0.5rem',
    borderRadius: '8px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: '2px solid #34A290 !important',
    },
    '.MuiOutlinedInput-root': {
      borderRadius: '8px',
      minHeight: '3.45rem',
    },
    '& .MuiInputBase-input': {
      fontWeight: '400',
    },
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    lineHeight: '140%',
  },
  selectLabel: {
    marginBottom: '0.5rem',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '160%',
  },
  selectInput: {
    borderRadius: '8px',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '140%',
  },
  stockDataLabel: {
    marginBottom: '0.5rem',
    color: '#162523',
    fontSize: '14px',
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: '160%',
  },

  chip: {
    borderRadius: '4px',
    background: '#DEF1E9',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '140%',
  },
  chipIcon: {
    color: '#162523 !important',
  },
  stockDataPlaceholder: {
    '.MuiInputBase-input': {
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '140%',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#162523',
      opacity: 1,
    },
  },
  stockDataUnderLabel: {
    marginTop: '1rem',
    color: 'grey',
    position: 'relative',
  },
  stockDataListItem: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    width: '100%',
    boxSizing: 'border-box',
  },
  stockDataListItemTicker: {
    color: '#819190',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '140%',
  },
  formHelperText: {
    marginTop: '0.5rem',
    marginLeft: '0',
    color: '#819190',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '140%',
  },
  stockDataOption: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: '1 0 0',
  },
  brandColorLabel: {
    marginBottom: '0.5rem',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '160%',
  },
  brandColorSubtext: {
    marginTop: '0.25rem',
    color: '#819190',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '140%',
  },
  formSection: {
    marginBottom: '2rem',
  },
  brandColorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  brandColorInput: {
    flex: 1,
    height: '3.45rem',
    borderRadius: '8px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: '2px solid #34A290 !important',
    },
    '.MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
    '& .MuiInputBase-input': {
      fontWeight: '400',
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      lineHeight: '140%',
    },
  },
  colorPreview: {
    width: '3.45rem',
    height: '3.45rem',
    borderRadius: '8px',
    border: '1px solid #DCE2E0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const CompanyDetails = ({company}) => {
  const [companies, setCompanies] = useState([]);
  const [industry, setIndustry] = useState('');
  const [fiscalYearEnd, setFiscalYearEnd] = useState();
  const [publicPeerCompanies, setPublicPeerCompanies] = useState([]);
  const [brandColorHex, setBrandColorHex] = useState('#DEF1E9');

  // Snackbar state - same as UserDetails
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' or 'error'
  });
  const {data: stockMarketData, isSuccess: isStockMarketDataSuccess} =
    useGetStockMarketDataQuery();
  const [updateOrg] = useUpdateOrgMutation();
  const {data: org, isSuccess: isOrgSuccess, refetch} = useGetOrgQuery();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDateChange = async (event) => {
    setFiscalYearEnd(event);
    const day = `${event?.$D}`;
    const month = `${event?.$M + 1}`;
    const formattedDay = day?.length === 2 ? day : `0${day}`;
    const formattedMonth = month?.length === 2 ? month : `0${month}`;
    const formattedFiscalYearEnd = `${formattedMonth}-${formattedDay}`;
    await updateOrg({
      fiscalYearEnd: formattedFiscalYearEnd,
    });
    await refetch();
    setSnackbar({
      open: true,
      message: 'Company Details saved!',
      severity: 'success',
    });
  };

  const handleIndustryChange = async (event) => {
    const industry = event.target.value;
    setIndustry(industry);
    await updateOrg({
      industry,
    });
    await refetch();
    setSnackbar({
      open: true,
      message: 'Company Details saved!',
      severity: 'success',
    });
  };

  const handlePublicCompanyChange = async (_, value) => {
    setPublicPeerCompanies(value);
    await updateOrg({
      publicPeerCompanies: value,
    });
    await refetch();
    setSnackbar({
      open: true,
      message: 'Company Details saved!',
      severity: 'success',
    });
  };

  const updateBrandColorAndRefresh = async (color) => {
    await updateOrg({brandColor: color});
    await refetch();
    setSnackbar({
      open: true,
      message: 'Company Details saved!',
      severity: 'success',
    });
    // Auto-refresh browser to apply brand color changes
    setTimeout(() => {
      window.location.reload();
    }, 500); // Small delay to show the toast first
  };

  const saveBrandColor = async () => {
    await updateBrandColorAndRefresh(brandColorHex);
  };

  const resetToDefault = async () => {
    const defaultColor = '#DEF1E9';
    setBrandColorHex(defaultColor);
    await updateBrandColorAndRefresh(defaultColor);
  };

  useEffect(() => {
    if (isOrgSuccess) {
      const {industry, fiscalYearEnd, publicPeerCompanies, brandColor} = org;
      const fiscalYearEndDate = dayjs(fiscalYearEnd);
      setIndustry(industry);
      setFiscalYearEnd(fiscalYearEndDate);
      setPublicPeerCompanies(publicPeerCompanies || []);
      const isValidHex =
        typeof brandColor === 'string' &&
        /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(brandColor);
      setBrandColorHex(isValidHex ? brandColor : '#DEF1E9');
    }
  }, [isOrgSuccess, org]);

  useEffect(() => {
    if (isStockMarketDataSuccess) {
      const companies = Object.keys(stockMarketData).map((key) => ({
        ticker: stockMarketData[key].ticker,
        name: stockMarketData[key].title,
      }));
      setCompanies(companies);
    }
  }, [isStockMarketDataSuccess]);

  return (
    <Box>
      <SettingsCard>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '24px',
            color: '#162523',
          }}
        >
          Basic settings
        </Typography>
        <Grid container spacing={4}>
          {/* Year End Date - Top Left */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.formSection}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Typography variant={'subtitle1'} sx={styles.selectLabel}>
                  Year end date
                </Typography>
                <DateField
                  format="MM/DD"
                  sx={styles.datePicker}
                  onChange={handleDateChange}
                  value={fiscalYearEnd}
                />
              </LocalizationProvider>
            </Box>
          </Grid>

          {/* Industry - Top Right */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.formSection}>
              <FormControl fullWidth>
                <Typography variant={'subtitle1'} sx={styles.selectLabel}>
                  Industry
                </Typography>
                <Select
                  displayEmpty
                  sx={styles.select}
                  id={'company-details-industry-select'}
                  onChange={handleIndustryChange}
                  value={industry || ''}
                  renderValue={(selected) => {
                    if (selected?.length === 0) {
                      return 'Select industry';
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: styles.selectInput,
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    Select industry
                  </MenuItem>
                  {INDUSTRY_OPTIONS.map((value, key) => (
                    <MenuItem key={key} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Peer Companies - Bottom Left */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.formSection}>
              <FormControl fullWidth>
                <Typography variant={'subtitle1'} sx={styles.stockDataLabel}>
                  Publicly traded peer companies
                </Typography>
                <Autocomplete
                  multiple
                  filterSelectedOptions
                  id="tags-outlined"
                  value={publicPeerCompanies}
                  onChange={handlePublicCompanyChange}
                  options={companies}
                  disabled={!isStockMarketDataSuccess}
                  sx={styles.peerCompaniesAutocomplete}
                  slotProps={{
                    paper: {
                      sx: {
                        borderRadius: '8px',
                      },
                    },
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.ticker === value.ticker
                  }
                  getOptionLabel={(option) =>
                    `${option.name} (${option.ticker})`
                  }
                  getOptionDisabled={() => publicPeerCompanies?.length >= 5}
                  renderOption={(props, option) => {
                    return (
                      <li
                        {...props}
                        key={option.ticker}
                        style={styles.stockDataListItem}
                      >
                        <span>{option.name}</span>
                        <span style={styles.stockDataListItemTicker}>
                          {option.ticker}
                        </span>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) => {
                    return value.map((option, index) => (
                      <Chip
                        key={option.ticker}
                        label={option.ticker}
                        {...getTagProps(index)}
                        sx={styles.chip}
                        deleteIcon={<CloseIcon sx={styles.chipIcon} />}
                      />
                    ));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={styles.stockDataPlaceholder}
                      placeholder={
                        publicPeerCompanies?.length === 0 ? 'Add companies' : ''
                      }
                      helperText="Add up to 5"
                      FormHelperTextProps={{
                        sx: styles.formHelperText,
                      }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Grid>

          {/* Brand Color - Bottom Right */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.formSection}>
              <FormControl fullWidth>
                <Typography variant={'subtitle1'} sx={styles.brandColorLabel}>
                  Company brand color
                </Typography>
                <Box sx={styles.brandColorContainer}>
                  <TextField
                    value={brandColorHex}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBrandColorHex(val);
                    }}
                    onBlur={saveBrandColor}
                    placeholder="#DEF1E9"
                    sx={styles.brandColorInput}
                    inputProps={{maxLength: 7}}
                  />
                  <Box
                    sx={{
                      ...styles.colorPreview,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    style={{backgroundColor: brandColorHex}}
                  />
                  <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => {
                      setAnchorEl(null);
                      saveBrandColor();
                    }}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                  >
                    {(() => {
                      const [color, setColor] = useColor(brandColorHex);
                      return (
                        <Box sx={{p: 2}}>
                          <ColorPicker
                            key={brandColorHex}
                            color={color}
                            onChange={setColor}
                            onChangeComplete={(newColor) => {
                              setBrandColorHex(newColor.hex);
                            }}
                            hideAlpha
                            hideInput={['rgb', 'hsv']}
                          />
                        </Box>
                      );
                    })()}
                  </Popover>
                </Box>
                <Typography variant={'body2'} sx={styles.brandColorSubtext}>
                  Used for financial table racing stripes. Choose a light color
                  for readability. Change will be applied to all documents.{' '}
                  <span
                    onClick={resetToDefault}
                    style={{
                      color: '#34A290',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Reset to default
                  </span>
                </Typography>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </SettingsCard>

      {/* Snackbar for success and error messages - same style as UserDetails */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        style={{marginTop: '35px'}}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
          severity={snackbar.severity}
          icon={
            snackbar.severity === 'success' ? (
              <Box
                sx={{
                  backgroundColor: '#26A993',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CheckIcon sx={{color: 'white', fontSize: 16}} />
              </Box>
            ) : (
              <ErrorOutlineIcon sx={{color: '#D73E3E', fontSize: 22}} />
            )
          }
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '1px solid #E1E5E4',
            borderRadius: '8px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

CompanyDetails.propTypes = {
  company: PropTypes.shape({
    orgId: PropTypes.string.isRequired,
    orgName: PropTypes.string.isRequired,
  }).isRequired,
};

export default CompanyDetails;
